import { useEffect } from 'react'

type WakeLockSentinelLike = { release(): Promise<void>; addEventListener(t: string, fn: () => void): void }
type WakeLockLike = { request(type: 'screen'): Promise<WakeLockSentinelLike> }

/**
 * Keeps the screen awake during a game where the API exists. Safari only shipped
 * Screen Wake Lock in 16.4, so on 16.0-16.3 this is a no-op and the iPad's
 * Auto-Lock setting is the only defence. Re-acquires after a tab switch, since
 * the lock is dropped whenever the document becomes hidden.
 */
export function useWakeLock(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return
    const wakeLock = (navigator as unknown as { wakeLock?: WakeLockLike }).wakeLock
    if (!wakeLock) return

    let sentinel: WakeLockSentinelLike | null = null
    let cancelled = false

    const acquire = async () => {
      if (cancelled || document.visibilityState !== 'visible') return
      try {
        sentinel = await wakeLock.request('screen')
      } catch {
        // Denied (low battery, or the gesture requirement was not met). Not fatal.
      }
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void acquire()
    }

    void acquire()
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', onVisibility)
      void sentinel?.release().catch(() => {})
    }
  }, [enabled])
}
